import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight, AlertCircle } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getHub, conditionsForHub, HUBS } from "@/data/care";
import { medicalWebPageJsonLd, faqPageJsonLd, breadcrumbListJsonLd } from "@/data/schema";
import { WHATSAPP_LINK } from "@/lib/api";

export default function Hub() {
  const { slug } = useParams();
  const hub = getHub(slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (!hub) return <Navigate to="/care" replace />;

  const conditions = conditionsForHub(hub.slug);
  const faqPool = conditions.flatMap((c) => (c.faqs || []).map((f, i) => ({ ...f, id: `${c.slug}-${i}`, condition: c.name })));

  return (
    <div data-testid={`hub-page-${hub.slug}`} className="pt-28 md:pt-36 pb-28">
      <SEO
        title={`${hub.name} — ${hub.kicker} · Dr Tegbir Vascular, Mohali`}
        description={hub.intro}
        keywords={hub.keywords}
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: hub.name, description: hub.intro, url: `/care/${hub.slug}`, about: hub.name }),
        faqPageJsonLd(faqPool.slice(0, 6)),
        breadcrumbListJsonLd([{ to: "/", label: "Home" }, { to: "/care", label: "Care" }, { label: hub.name }]),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { to: "/care", label: "Care" }, { label: hub.name }]} />
        </FadeUp>

        {/* Hero */}
        <div ref={heroRef} className="mt-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <FadeUp>
              <div className="kbd">— {hub.kicker}</div>
            </FadeUp>
            <h1 className="mt-8 font-serif text-6xl md:text-[7vw] leading-[0.96] tracking-tightest">
              <MaskedReveal text={hub.name} stagger={0.06} />
            </h1>
            <FadeUp delay={0.3} className="mt-10">
              <p className="text-xl md:text-2xl font-serif italic text-ink leading-snug max-w-xl">
                "{hub.lede}"
              </p>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{hub.intro}</p>
            </FadeUp>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8">
            <div className="aspect-[3/4] overflow-hidden hairline relative">
              <motion.img
                src={hub.image}
                alt={hub.imageAlt}
                style={{ y }}
                className="w-full h-full object-cover"
              />
              <div className="noise-overlay absolute inset-0" />
              <div className="absolute bottom-4 left-4 text-bone">
                <div className="kbd text-white/70">{hub.kicker}</div>
                <div className="font-serif text-lg mt-1">{hub.name}</div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"/>
            </div>
          </div>
        </div>

        {/* Conditions list */}
        <div className="mt-32 md:mt-40">
          <FadeUp>
            <div className="grid grid-cols-12 gap-6 mb-16 items-end">
              <div className="col-span-12 md:col-span-7">
                <div className="kbd">— Conditions in this hub</div>
                <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tightest">
                  {conditions.length} conditions we treat.
                </h2>
              </div>
              <div className="col-span-12 md:col-span-5 md:text-right">
                <Link to="/care" className="kbd hover:text-ink">← All hubs</Link>
              </div>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 hairline">
            {conditions.map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-5% 0px" }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.03 }}
                className="bg-bone"
              >
                <Link
                  to={`/conditions/${c.slug}`}
                  data-testid={`condition-card-${c.slug}`}
                  className="group block p-6 md:p-8 h-full hover:bg-white transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="kbd">Condition · {String(i + 1).padStart(2, "0")}</div>
                      <h3 className="mt-3 font-serif text-2xl md:text-3xl tracking-tightest group-hover:text-crimson transition-colors">
                        {c.name}
                      </h3>
                      <p className="mt-3 text-sm text-ink-muted leading-relaxed max-w-md">{c.oneLiner}</p>
                    </div>
                    <ArrowUpRight size={18} className="text-ink-muted group-hover:text-crimson group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                  {c.whenUrgent && (
                    <div className="mt-4 inline-flex items-center gap-2 text-xs text-crimson">
                      <AlertCircle size={12} /> Time-sensitive signs
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        {faqPool.length > 0 && (
          <div className="mt-32 md:mt-40 grid grid-cols-12 gap-8">
            <FadeUp className="col-span-12 md:col-span-4">
              <div className="kbd">— Frequently asked</div>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tightest leading-[1.05]">
                Questions we hear<br/><span className="italic font-normal">most often.</span>
              </h2>
            </FadeUp>
            <FadeUp className="col-span-12 md:col-span-8" delay={0.15}>
              <Accordion type="single" collapsible data-testid={`hub-faqs-${hub.slug}`} className="hairline-t">
                {faqPool.slice(0, 6).map((f) => (
                  <AccordionItem key={f.id} value={f.id} className="border-b border-black/10">
                    <AccordionTrigger data-testid={`faq-${f.id}`} className="text-left text-lg md:text-xl font-serif tracking-tightest py-6">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-ink-muted leading-relaxed pb-6">
                      {f.a}
                      <div className="mt-3 kbd">Context: {f.condition}</div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeUp>
          </div>
        )}

        {/* Related hubs */}
        <div className="mt-32">
          <FadeUp>
            <div className="hairline-t pt-12">
              <div className="kbd">— Other hubs</div>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl tracking-tightest">Continue exploring</h3>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4">
                {HUBS.filter((h) => h.slug !== hub.slug).map((h) => (
                  <Link key={h.slug} to={`/care/${h.slug}`} className="group py-3 border-t border-black/10 hover:border-crimson transition-colors">
                    <div className="kbd">{h.kicker}</div>
                    <div className="mt-1 font-serif text-lg leading-tight group-hover:text-crimson">{h.name}</div>
                  </Link>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        {/* CTA */}
        <FadeUp className="mt-24">
          <div className="hairline-t pt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-serif text-4xl md:text-6xl tracking-tightest leading-[1.02] max-w-3xl">
                Wondering if this is your case?
              </h3>
              <p className="mt-4 text-ink-muted max-w-xl">Send us your imaging and the team will get back to you within one working hour with a view on next steps.</p>
            </div>
            <div className="col-span-12 md:col-span-4 flex flex-wrap gap-3 items-end md:justify-end">
              <Link to="/contact" className="btn-pill-inv">Book consultation <ArrowUpRight size={14}/></Link>
              <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello Dr Sidhu, I have a question about ${hub.name}.`)} className="btn-pill">WhatsApp <ArrowUpRight size={14}/></a>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
